import { useLayoutEffect, useRef } from "react";
import SkinLogo from "./skin-logo";

const UNLOCK_LABEL_BY_STEP = [
  "default",
  "trial-mode",
  "trial-days",
  "usage-reset",
  // Only cycles in once the dashboard's cap offer applies to this install.
  "promo",
];

function syncUnlockButtonWidth(button, step) {
  if (!button) return;
  const kind = UNLOCK_LABEL_BY_STEP[step] || "default";
  const active = button.querySelector(
    `.subscription-status-button__label--${kind}`,
  );
  if (!active) return;

  const styles = getComputedStyle(button);
  const pad =
    (parseFloat(styles.paddingLeft) || 0) +
    (parseFloat(styles.paddingRight) || 0);
  const next = Math.ceil(active.scrollWidth + pad);
  if (!Number.isFinite(next) || next <= 0) return;
  if (button.style.width === `${next}px`) return;
  button.style.width = `${next}px`;
}

export default function PopupHeader({
  skin,
  announcement,
  isPro,
  trialMode,
  trialEnded,
  remainingTrialDays,
  usageHit,
  usageRemaining,
  usagePeriod,
  subscriptionStatusStep,
  // The dashboard's cap-triggered offer ({ label, afterCaps }) and whether
  // this install has hit enough weekly caps to qualify for it.
  usagePromo,
  promoQualified,
  musicProOnly,
  isSlowedEnabled,
  contentApplying,
  mount,
  billingLoading,
  upgradeError,
  onUpgradeClick,
  onManageSubscriptionClick,
  onDebugToggle,
  onSlowedToggle,
  hideManagePlan,
  onUsageClick,
}) {
  const unlockButtonRef = useRef(null);
  const hasTrial = trialMode && remainingTrialDays >= 0;
  const showUnlockButton = !isPro;
  const usageMinutesLeft = Math.max(
    0,
    Math.floor(Number(usageHit ? 0 : usageRemaining) || 0),
  );
  const usageText =
    usageMinutesLeft >= 60
      ? `${Math.floor(usageMinutesLeft / 60)}h ${usageMinutesLeft % 60}m left`
      : `${usageMinutesLeft} min left`;
  // Post-trial allowance: Unlock PRO → Usage mode → minutes left → Resets …,
  // including after the cap so 0 min left still cycles. Minutes left is the
  // smaller of the weekly and monthly windows, so name the window that is
  // actually holding it back — calling a monthly squeeze "weekly" made the
  // allowance look like a fraction of what it is.
  const usageResetText =
    usagePeriod === "monthly" ? "Resets monthly" : "Resets weekly";
  const hasUsage = !isPro && !hasTrial && trialEnded === true;
  const showUsageDepletedUnlock = hasUsage && usageHit === true;
  // The cap offer joins the rotation only for metered users who have hit
  // enough weekly caps to qualify. It never touches the free slow-down — it
  // only changes what the Unlock PRO button says and where it goes.
  const showPromo =
    hasUsage && promoQualified === true && Boolean(usagePromo?.label);
  const rotating = hasTrial || hasUsage;
  const maxStep = hasUsage ? (showPromo ? 4 : 3) : 2;
  const step = rotating ? Math.min(subscriptionStatusStep || 0, maxStep) : 0;
  const daysText =
    remainingTrialDays > 0
      ? `${remainingTrialDays} ${remainingTrialDays === 1 ? "day" : "days"} left`
      : "Last day";
  const statusText = hasTrial ? "Trial mode" : "Usage mode";
  const countdownText = hasTrial ? daysText : usageText;

  useLayoutEffect(() => {
    if (!showUnlockButton) return;
    syncUnlockButtonWidth(unlockButtonRef.current, step);
  }, [
    showUnlockButton,
    step,
    statusText,
    countdownText,
    usageResetText,
    rotating,
    hasUsage,
    showPromo,
  ]);

  return (
    <div className="header">
      <div className="header__content">
        <div className="header__left_button">
          {showUnlockButton && (
            <button
              type="button"
              ref={unlockButtonRef}
              className={`subscription-status-button subscription-status-button--unlock ${showUsageDepletedUnlock ? "subscription-status-button--unlock-depleted" : ""} subscription-status-button--step-${step}`}
              onClick={hasUsage && !showPromo ? onUsageClick : onUpgradeClick}
            >
              <span className="subscription-status-button__label subscription-status-button__label--default">
                Unlock PRO
              </span>
              {rotating && (
                <span className="subscription-status-button__label subscription-status-button__label--trial-mode">
                  {statusText}
                </span>
              )}
              {rotating && (
                <span className="subscription-status-button__label subscription-status-button__label--trial-days">
                  {countdownText}
                </span>
              )}
              {hasUsage && (
                <span className="subscription-status-button__label subscription-status-button__label--usage-reset">
                  {usageResetText}
                </span>
              )}
              {showPromo && (
                <span className="subscription-status-button__label subscription-status-button__label--promo">
                  {usagePromo.label}
                </span>
              )}
            </button>
          )}
          {upgradeError && (
            <button
              type="button"
              className="header__upgrade-error"
              onClick={onUpgradeClick}
              title="Try opening checkout again"
            >
              {upgradeError}
            </button>
          )}
          {isPro && !hideManagePlan && (
            <button
              type="button"
              className={`subscription-status-button${billingLoading ? " subscription-status-button--loading" : ""}`}
              disabled={billingLoading}
              aria-busy={billingLoading}
              onClick={onManageSubscriptionClick}
            >
              {billingLoading ? (
                <span className="subscription-status-button__loading">
                  <span className="header__spinner" aria-hidden="true" />
                  Loading
                </span>
              ) : (
                <span className="subscription-status-button__label subscription-status-button__label--default">
                  Manage plan
                </span>
              )}
            </button>
          )}
        </div>

        <div
          className="header__title"
          onDoubleClick={(e) => e.metaKey && onDebugToggle()}
        >
          <SkinLogo
            skin={skin}
            isPro={isPro}
            trialMode={trialMode}
            announcement={announcement}
          />
        </div>

        <div className="header__right_button">
          {contentApplying ? (
            <span className="header__applying" aria-live="polite">
              <span className="header__spinner" aria-hidden="true" />
              Applying
            </span>
          ) : musicProOnly ? (
            <span className="header__status header__status--muted">
              Pro only
            </span>
          ) : !isSlowedEnabled && mount ? (
            <span className="header__status">Off</span>
          ) : null}
          <input
            type="checkbox"
            className="switcher"
            checked={isSlowedEnabled}
            // Running out of free minutes must not reach this switch: slowing
            // a video down on regular YouTube is free at any meter reading, and
            // musicProOnly already covers the one place Pro is required to be
            // on at all (YouTube Music). See content/youtube/pro-lock.js.
            disabled={musicProOnly || contentApplying}
            onChange={() => onSlowedToggle(!isSlowedEnabled)}
          />
        </div>
      </div>

      <div className="header__bar" />
    </div>
  );
}
